import React from 'react';
import { resolveTheme, panelStyle, LABEL_COLORS } from '../themes';
import type { Theme } from '../themes';
import type { Cuboid } from '../../domain/entities/Cuboid';

interface SidePanelProps {
  theme?: string | Theme;
  cuboid: Cuboid | null;
  pinned?: boolean;
  onClose?: () => void;
}

function Row({ k, v }: { k: string; v: string | number }) {
  return (
    <div className="panel-row">
      <span className="panel-row-label">{k}</span>
      <span className="panel-row-value">{v}</span>
    </div>
  );
}

export function SidePanel({ theme, cuboid, pinned, onClose }: SidePanelProps) {
  const T = resolveTheme(theme);
  if (!cuboid) return null;

  const labelColor = LABEL_COLORS[cuboid.info.label] ?? T.accent2;

  return (
    <div
      aria-live="polite"
      className={`side-panel${pinned ? ' side-panel--pinned' : ''}`}
      style={{
        ...panelStyle(T, { overflow: 'hidden', width: 'min(820px, 92vw)' }),
        '--panel-rule': T.rule,
        '--panel-text': T.text,
        '--panel-text-dim': T.textDim,
        '--panel-accent': T.accent,
        '--panel-accent-border': `${T.accent}44`,
        '--panel-accent-bg': `${T.accent}24`,
        '--panel-font': T.font,
        '--panel-header-weight': String(T.headerWeight),
        '--panel-label-color': labelColor,
      } as React.CSSProperties}
    >
      <div className="panel-header">
        <i className="panel-label-dot" />
        <span className="panel-label-text">{cuboid.info.label}</span>
        <span className="panel-uuid">{cuboid.uuid}</span>
        <span className="panel-actions">
          <span className={`panel-badge ${cuboid.info.stationary ? 'panel-badge--stationary' : 'panel-badge--moving'}`}>
            {cuboid.info.stationary ? 'STATIONARY' : 'MOVING'}
          </span>
          {pinned && onClose && (
            <button onClick={onClose} aria-label="Close panel" className="panel-close-btn">
              ✕
            </button>
          )}
        </span>
      </div>
      <div className="panel-grid">
        <div className="panel-grid-col">
          <div className="panel-section-title">Position (m)</div>
          <Row k="x" v={cuboid.position.x.toFixed(3)} />
          <Row k="y" v={cuboid.position.y.toFixed(3)} />
          <Row k="z" v={cuboid.position.z.toFixed(3)} />
        </div>
        <div className="panel-grid-col">
          <div className="panel-section-title">Dimensions (m)</div>
          <Row k="width" v={cuboid.dimensions.width.toFixed(2)} />
          <Row k="length" v={cuboid.dimensions.height.toFixed(2)} />
          <Row k="height" v={cuboid.dimensions.depth.toFixed(2)} />
        </div>
        <div className="panel-grid-col">
          <div className="panel-section-title">Pose & Sensor</div>
          <Row k="yaw" v={`${cuboid.transform.yaw.toFixed(3)} rad`} />
          <Row k="cam" v={cuboid.info.cameraUsed} />
          <Row k="sensor" v={cuboid.info.sensorId} />
        </div>
      </div>
    </div>
  );
}
